import { z } from "zod"
import { Show } from "@clerk/react"
import Markdown from "react-markdown"
import { useConversations, useConversation, useMimirClient } from "@mimir/api-client"
import type { ChunkResult, WebResult, Message } from "@mimir/types"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState, useRef, useCallback } from "react"
import { toast } from "sonner"
import { useUser } from "@clerk/react"
import { Link, Pencil, Copy, Check, ArrowUpRight } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { ChatInput } from "@/components/chat-input"
import type { SearchMode } from "@/components/chat-input"
import { MimirLogo } from "@/components/mimir-logo"
import { getContentTypeColor, getContentTypeIcon } from "@/lib/content-types"
import { codeToHtml } from "@/lib/shiki.bundle"
import type { BundledLanguage } from "@/lib/shiki.bundle"

export const Route = createFileRoute("/_app/chat")({
  validateSearch: z.object({
    conversationId: z.string().optional(),
    q: z.string().optional(),
  }),
  component: ChatPage,
})

interface Turn {
  role: "user" | "assistant"
  content: string
  chunks?: ChunkResult[]
  webResults?: WebResult[]
  webSearched?: boolean
}

const LOADING_VERBS = [
  "Thinking",
  "Ideating",
  "Recalling",
  "Remembering",
  "Fetching wisdom from Mímisbrunnr",
  "Connecting the dots",
  "Navigating the Mind Palace",
  "Pondering",
  "Perusing",
  "Translating ancient runes",
  "Consulting the Norns",
  "Cooking",
]

// ---------------------------------------------------------------------------
// Shiki code block — async highlight, fallback to plain while loading
// ---------------------------------------------------------------------------
function ShikiCode({ code, lang }: { code: string; lang: string }) {
  const [html, setHtml] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const l = lang as BundledLanguage
    codeToHtml(code, { lang: l || "text", theme: "vesper" })
      .then(setHtml)
      .catch(() => setHtml(null))
  }, [code, lang])

  const copy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [code])

  return (
    <div className="relative group/code my-3 rounded-xl overflow-hidden border border-border/60">
      {lang && (
        <div className="flex items-center justify-between px-3.5 py-1.5 bg-muted border-b border-border">
          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
            {lang}
          </span>
          <button
            type="button"
            onClick={copy}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
      {html ? (
        <div
          className="[&>pre]:m-0! [&>pre]:p-4 [&>pre]:text-[12.5px] [&>pre]:leading-[1.65] [&>pre]:overflow-x-auto [&>pre]:font-mono"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="bg-muted text-foreground p-4 text-[12.5px] leading-[1.65] overflow-x-auto font-mono m-0">
          <code>{code}</code>
        </pre>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Markdown renderer with custom code blocks
// ---------------------------------------------------------------------------
const markdownComponents = {
  code({ className, children, ...props }: React.ComponentProps<"code"> & { inline?: boolean }) {
    const match = /language-(\w+)/.exec(className || "")
    const lang = match?.[1] ?? ""
    const code = String(children).replace(/\n$/, "")
    const isBlock = !props.inline && (match || code.includes("\n"))
    if (isBlock) return <ShikiCode code={code} lang={lang} />
    return (
      <code
        className="bg-muted/80 border border-border/50 px-1.5 py-0.5 rounded text-[12px] font-mono"
        {...props}
      >
        {children}
      </code>
    )
  },
  // Tighten paragraph spacing inside chat
  p({ children }: React.ComponentProps<"p">) {
    return <p className="mb-3 last:mb-0 leading-[1.65]">{children}</p>
  },
}

// ---------------------------------------------------------------------------
// Loading indicator — ambient, no bubble
// ---------------------------------------------------------------------------
function LoadingIndicator({ status }: { status?: string }) {
  const [verbIdx, setVerbIdx] = useState(0)
  useEffect(() => {
    if (status) return
    const t = setInterval(() => setVerbIdx((i) => (i + 1) % LOADING_VERBS.length), 2500)
    return () => clearInterval(t)
  }, [status])
  const label = status ?? `${LOADING_VERBS[verbIdx]}…`
  return (
    <div className="flex items-center gap-3 px-1">
      <motion.div
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className="shrink-0"
      >
        <MimirLogo iconOnly className="h-4 text-foreground/70" />
      </motion.div>
      <AnimatePresence mode="wait">
        <motion.span
          key={label}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.2 }}
          className="text-[12px] text-muted-foreground/50 italic"
        >
          {label}
        </motion.span>
      </AnimatePresence>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Citation cards
// ---------------------------------------------------------------------------
function CitationCards({
  chunks,
  webResults,
}: {
  chunks: ChunkResult[]
  webResults?: WebResult[]
}) {
  const navigate = useNavigate()
  const seen = new Set<string>()
  const unique = chunks.filter((c) => {
    if (seen.has(c.document_id)) return false
    seen.add(c.document_id)
    return true
  })
  const validWebResults = webResults?.filter((w) => !!w.url) ?? []
  if (!unique.length && !validWebResults.length) return null
  return (
    <div className="mt-3 flex flex-col gap-2">
      <span className="text-[10px] font-mono text-muted-foreground/40 uppercase tracking-widest">
        Sources
      </span>
      <div className="flex flex-wrap gap-2">
        {unique.map((c) => {
          const Icon = getContentTypeIcon(c.content_type)
          const colorClass = getContentTypeColor(c.content_type)
          return (
            <button
              key={c.document_id}
              type="button"
              onClick={() =>
                navigate({ to: "/mind-palace/$documentId", params: { documentId: c.document_id } })
              }
              className="group/chip flex items-center gap-2 px-3 py-2 bg-background/50 border border-border rounded-lg hover:bg-muted/40 transition-colors max-w-[200px]"
            >
              <span className={`shrink-0 ${colorClass}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <span className="text-[12px] font-medium text-foreground truncate">
                {c.title || "Untitled"}
              </span>
              <ArrowUpRight className="w-3 h-3 text-muted-foreground shrink-0 opacity-0 group-hover/chip:opacity-100 transition-opacity ml-auto" />
            </button>
          )
        })}
        {validWebResults.map((w) => (
          <a
            key={w.url}
            href={w.url}
            target="_blank"
            rel="noreferrer"
            className="group/chip flex items-center gap-2 px-3 py-2 bg-background/50 border border-border rounded-lg hover:bg-muted/40 transition-colors max-w-[200px]"
          >
            <span className="shrink-0 text-blue-500">
              <Link className="w-3.5 h-3.5" />
            </span>
            <span className="text-[12px] font-medium text-foreground truncate">
              {w.title ||
                (() => {
                  try {
                    return new URL(w.url).hostname
                  } catch {
                    return w.url
                  }
                })()}
            </span>
            <ArrowUpRight className="w-3 h-3 text-muted-foreground shrink-0 opacity-0 group-hover/chip:opacity-100 transition-opacity ml-auto" />
          </a>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ turn, onEdit }: { turn: Turn; onEdit?: (newContent: string) => void }) {
  const isUser = turn.role === "user"
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(turn.content)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditing(false)
    setDraft(turn.content)
  }, [turn.content])

  const startEdit = () => {
    setDraft(turn.content)
    setEditing(true)
    setTimeout(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.selectionStart = el.selectionEnd = el.value.length
    }, 0)
  }

  const cancel = () => {
    setDraft(turn.content)
    setEditing(false)
  }

  const submit = () => {
    const trimmed = draft.trim()
    if (!trimmed || trimmed === turn.content) {
      cancel()
      return
    }
    setEditing(false)
    onEdit?.(trimmed)
  }

  if (isUser) {
    return (
      <div className="flex flex-col items-end gap-1">
        {editing ? (
          <div className="flex flex-col gap-2 w-full max-w-[75%]">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
                if (e.key === "Escape") cancel()
              }}
              className="w-full rounded-2xl rounded-br-sm bg-muted/60 border border-primary/40 ring-1 ring-primary/30 text-foreground px-4 py-2.5 text-[13px] leading-[1.6] resize-none field-sizing-content max-h-[200px] overflow-y-auto outline-none font-sans"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={cancel}
                className="text-[11px] px-3 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                className="text-[11px] px-3 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        ) : (
          <div className="group/user flex flex-col max-w-[75%] items-end gap-1">
            <div className="bg-muted/60 border border-border/60 rounded-2xl rounded-br-sm px-4 py-2.5 text-[13px] leading-[1.6] text-foreground">
              {turn.content}
            </div>
            {onEdit && (
              <button
                type="button"
                onClick={startEdit}
                className="opacity-0 group-hover/user:opacity-100 transition-opacity flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground px-1 py-0.5"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  // Assistant
  return (
    <div className="flex flex-col gap-1 items-start">
      {turn.content && (
        <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest px-1 mb-1">
          Mímir
        </span>
      )}
      <div className="w-full pl-4 bg-background rounded-2xl">
        <div className="prose prose-sm dark:prose-invert max-w-none text-[13.5px] leading-[1.65] text-foreground [&_a]:text-primary [&_a]:underline [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_blockquote]:border-l-2 [&_blockquote]:border-primary/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground">
          <Markdown components={markdownComponents as Record<string, React.ElementType>}>
            {turn.content.replace(/\s*\[Source \d+\]/g, "")}
          </Markdown>
        </div>
      </div>
      {turn.chunks?.length || turn.webResults?.length ? (
        <CitationCards chunks={turn.chunks ?? []} webResults={turn.webResults} />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chat page
// ---------------------------------------------------------------------------
function ChatPage() {
  const { conversationId, q } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState(q ?? "")
  const [searchMode, setSearchMode] = useState<SearchMode>("auto")
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamStatus, setStreamStatus] = useState<string | undefined>()
  const client = useMimirClient()
  const abortRef = useRef<AbortController | null>(null)
  const activeConversation = useConversation(conversationId ?? null)
  const conversations = useConversations()
  const { user } = useUser()
  const name = user?.firstName || user?.fullName || ""
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeConversation.data && conversationId) {
      const loaded = activeConversation.data.messages
        .filter((m: Message) => m.role !== "system")
        .map((m: Message): Turn => {
          const tr = m.tool_results as Record<string, unknown> | null | undefined
          return {
            role: m.role as "user" | "assistant",
            content: m.content,
            chunks: (tr?.chunks as ChunkResult[] | undefined) ?? [],
            webResults: (tr?.web_results as WebResult[] | undefined) ?? [],
            webSearched: tr?.web_searched === true,
          }
        })
      setTurns(loaded)
    } else if (!conversationId) {
      setTurns([])
    }
  }, [activeConversation.data, conversationId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [turns, isStreaming])

  const runStream = useCallback(
    async (question: string, signal: AbortSignal) => {
      setTurns((t) => [
        ...t,
        { role: "assistant", content: "", chunks: [], webResults: [], webSearched: false },
      ])
      // Delay navigate until done so useConversation doesn't fetch mid-stream and reset turns
      let pendingConversationId: string | null = null
      try {
        for await (const event of client.queryStream(
          { question, conversation_id: conversationId || null, top_k: 10, search_mode: searchMode },
          signal,
        )) {
          if (event.type === "metadata") {
            if (!conversationId) pendingConversationId = event.conversation_id
          } else if (event.type === "status") {
            setStreamStatus(event.message)
          } else if (event.type === "delta") {
            setStreamStatus(undefined)
            setTurns((t) => {
              const last = t[t.length - 1]
              if (!last || last.role !== "assistant") return t
              return [...t.slice(0, -1), { ...last, content: last.content + event.text }]
            })
          } else if (event.type === "done") {
            setTurns((t) => {
              const last = t[t.length - 1]
              if (!last || last.role !== "assistant") return t
              return [
                ...t.slice(0, -1),
                {
                  ...last,
                  chunks: event.chunks_used,
                  webResults: event.web_results,
                  webSearched: event.web_searched,
                },
              ]
            })
            if (pendingConversationId) {
              navigate({ search: { conversationId: pendingConversationId }, replace: true })
            }
            conversations.refetch()
          } else if (event.type === "error") {
            toast.error(event.message)
            setTurns((t) => t.slice(0, -1))
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== "AbortError") {
          toast.error(err.message)
          setTurns((t) => {
            const last = t[t.length - 1]
            return last?.role === "assistant" && last.content === "" ? t.slice(0, -1) : t
          })
        }
      } finally {
        setIsStreaming(false)
        setStreamStatus(undefined)
      }
    },
    [client, conversationId, searchMode, navigate, conversations],
  )

  const onSend = () => {
    const question = draft.trim()
    if (!question) return
    setTurns((t) => [...t, { role: "user", content: question }])
    setDraft("")
    const controller = new AbortController()
    abortRef.current = controller
    setIsStreaming(true)
    runStream(question, controller.signal)
  }

  const onStop = () => {
    abortRef.current?.abort()
    setTurns((t) => {
      const last = t[t.length - 1]
      return last?.role === "assistant" && last.content === "" ? t.slice(0, -1) : t
    })
    setIsStreaming(false)
  }

  const onEdit = async (index: number, newContent: string) => {
    setTurns((t) => [...t.slice(0, index), { role: "user", content: newContent }])
    if (conversationId) {
      try {
        await client.truncateConversation(conversationId, index)
      } catch {
        /* non-fatal */
      }
    }
    const controller = new AbortController()
    abortRef.current = controller
    setIsStreaming(true)
    runStream(newContent, controller.signal)
  }

  return (
    <>
      <Show when="signed-out">
        <p className="text-sm text-muted-foreground">Sign in to chat.</p>
      </Show>
      <Show when="signed-in">
        <div className="flex flex-col h-[calc(100svh-5rem)]">
          {activeConversation.isError && conversationId && (
            <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center px-6">
              <p className="text-sm font-medium text-foreground">Couldn't load conversation</p>
              <p className="text-xs text-muted-foreground">
                It may have been deleted or something went wrong.
              </p>
            </div>
          )}
          {!activeConversation.isError && turns.length > 0 && (
            <div className="flex flex-1 flex-col overflow-y-auto min-h-0 py-8">
              <div className="flex flex-col gap-7 w-full max-w-3xl mx-auto px-6">
                {turns.map((t, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <MessageBubble
                      turn={t}
                      onEdit={t.role === "user" ? (newContent) => onEdit(i, newContent) : undefined}
                    />
                  </motion.div>
                ))}
                {isStreaming && turns.at(-1)?.content === "" && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <LoadingIndicator status={streamStatus} />
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
          {!activeConversation.isError && (
            <ChatInput
              value={draft}
              onChange={setDraft}
              onSend={onSend}
              onStop={onStop}
              isPending={isStreaming}
              searchMode={searchMode}
              onSearchModeChange={setSearchMode}
              disabled={isStreaming}
              autoFocus
              empty={turns.length === 0}
              greeting={
                turns.length === 0
                  ? `What would you like to know${name ? `, ${name}` : ""}?`
                  : undefined
              }
            />
          )}
        </div>
      </Show>
    </>
  )
}
